// Brand ink for light accents (e.g. lime). Matches the site's `lime-contrast`.
const DARK_INK = '#0d1117'
const LIGHT_INK = '#ffffff'

/**
 * Pick a readable foreground (text/icon) color to sit on top of `accent`.
 *
 * Bright accents like the brand lime (#c3f900) need dark ink; dark accents
 * like the default indigo (#4f46e5) need white. Uses the WCAG relative
 * luminance of the accent and a 0.55 threshold (tuned so saturated lime lands
 * on the dark side). Returns white for any input we can't parse.
 */
export function readableInk(accent: string): string {
  const rgb = parseHex(accent)
  if (!rgb) return LIGHT_INK
  return relativeLuminance(rgb) > 0.55 ? DARK_INK : LIGHT_INK
}

/** Same ink decision for an already-resolved rgb triple (non-hex CSS colors). */
export function readableInkFromRgb(rgb: [number, number, number]): string {
  return relativeLuminance(rgb) > 0.55 ? DARK_INK : LIGHT_INK
}

/**
 * Resolve any CSS color (oklch, color-mix, named…) to an rgb triple by
 * letting the browser compute it on a probe element. Null if unparseable.
 */
export function resolveCssColor(color: string): [number, number, number] | null {
  try {
    const probe = document.createElement('div')
    probe.style.display = 'none'
    probe.style.color = color
    document.body.appendChild(probe)
    const computed = getComputedStyle(probe).color
    probe.remove()
    const m = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null
  }
  catch {
    return null
  }
}

function parseHex(input: string): [number, number, number] | null {
  let hex = input.trim().replace(/^#/, '')
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('')
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ]
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const [rl, gl, bl] = [r, g, b].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl
}
